import { resolveRepInitials } from '../utils/repInitials';

// Helper function to format currency with commas
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  // Ensure we have a clean number, remove any existing commas or dollar signs
  const cleanAmount = typeof amount === 'string' ? 
    parseFloat(amount.replace(/[$,]/g, '')) : 
    Number(amount);
  
  if (isNaN(cleanAmount)) {
    return '0.00';
  }
  
  return cleanAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// First define the helper function (unchanged - preserves cost item / cost group / pricing rendering)
const generateItemsHtml = (items, displaySettings = {}) => {
  if (!items || !items.length) return '';

  const {
    showItem = true,
    showQuantity = true,
    showUnit = true,
    showPrice = true,
    showCostCodes = true,
    showScopeDescription = true,
    showCostCodeDescription = true,
    showTotalCost = true,
    expandGroups = false,
    separateGroups = false
  } = displaySettings;

  // Helper function to convert description text to HTML list
  const formatDescriptionAsList = (description) => {
    if (!description) return '';
    
    // Split by newlines or periods followed by a space
    const points = description
      .split(/\n|(?<=\.)\s+/)
      .filter(point => point.trim().length > 0);
    
    if (points.length <= 1) {
      return `<div class="text-base text-gray-700 mt-3 leading-relaxed">${description}</div>`;
    }
    
    return `
      <ul class="list-disc pl-5 mt-3 space-y-1">
        ${points.map(point => `<li class="text-base text-gray-700 leading-relaxed">${point.trim()}</li>`).join('')}
      </ul>
    `;
  };

  let html = `
    <div class="mt-6">
      <table class="w-full border-collapse">
        <tbody>
  `;

  let grandTotal = 0;

  // Helper function to render photos for an item
  const renderPhotos = (item) => {
    if (!item.photos || item.photos.length === 0) return '';
    
    return `
      <tr>
        <td colspan="4" class="py-4">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            ${item.photos.map(photo => `
              <div class="rounded-lg overflow-hidden border border-gray-300">
                <img src="${photo.fileUrl}" alt="${photo.fileName || 'Scope photo'}" class="w-full h-48 object-cover" />
              </div>
            `).join('')}
          </div>
        </td>
      </tr>
    `;
  };

  // Helper function to render an item row
  const renderItem = (item, isGrouped = false, isSubItem = false) => {
    const itemTotal = item.totals?.clientPrice || item.clientPrice || 0;
    if (!isSubItem) grandTotal += itemTotal;

    const bgClass = isSubItem ? 'bg-gray-50' : '';
    const paddingClass = isSubItem ? 'pl-8' : '';
    const borderClass = isSubItem ? 'border-b border-gray-100' : 'border-b border-gray-200';

    let itemHtml = `
      <tr class="${borderClass} ${bgClass}">
        ${showItem ? `
          <td class="py-${isSubItem ? '4' : '5'} ${paddingClass}">
            <div class="font-${isSubItem ? 'medium' : 'semibold'} text-${isSubItem ? 'base' : 'lg'}">${item.item || ''}</div>
            ${(isSubItem ? showCostCodeDescription : showScopeDescription) && item.description ? formatDescriptionAsList(item.description) : ''}
          </td>
        ` : ''}
        ${showQuantity ? `
          <td class="text-right py-${isSubItem ? '4' : '5'} align-top text-base">${item.quantity || ''}</td>
        ` : ''}
        ${showUnit ? `
          <td class="text-right py-${isSubItem ? '4' : '5'} align-top text-base">${item.unit || ''}</td>
        ` : ''}
        ${showPrice ? `
          <td class="text-right py-${isSubItem ? '4' : '5'} align-top text-base ${isSubItem ? '' : 'font-medium'}">$${formatCurrency(isSubItem ? (item.clientPrice || 0) : itemTotal)}</td>
        ` : ''}
      </tr>
    `;

    // Add photos for scope items (not sub-items)
    if (!isSubItem && item.type === 'scope') {
      itemHtml += renderPhotos(item);
    }

    return itemHtml;
  };

  if (separateGroups) {
    // First show all grouped items (scope items)
    const groupedItems = items.filter(item => item.type === 'scope');
    const nonGroupedItems = items.filter(item => item.type === 'cost' && !item.isPartOfGroup);

    // Render grouped items
    if (groupedItems.length > 0) {
      groupedItems.forEach(item => {
        html += renderItem(item, true);
        
        // Show cost codes if expandGroups is enabled
        if (expandGroups && item.costCodes && item.costCodes.length > 0) {
          item.costCodes.forEach(costCode => {
            html += renderItem(costCode, true, true);
          });
        }
      });
    }

    // Add separator if both groups exist
    if (groupedItems.length > 0 && nonGroupedItems.length > 0) {
      html += `
        <tr>
          <td colspan="4" class="py-4">
            <hr class="border-gray-300">
          </td>
        </tr>
      `;
    }

    // Render non-grouped items
    if (nonGroupedItems.length > 0) {
      nonGroupedItems.forEach(item => {
        html += renderItem(item, false);
      });
    }
  } else {
    // Original logic: show all items together
    items.forEach(item => {
      if (item.type === 'scope' || !item.isPartOfGroup) {
        html += renderItem(item);

        // Display associated cost codes if expandGroups is enabled and this is a scope item
        if (expandGroups && item.costCodes && item.costCodes.length > 0) {
          item.costCodes.forEach(costCode => {
            html += renderItem(costCode, false, true);
          });
        }
      }
    });
  }

  // Add grand total - conditionally show total based on showTotalCost setting
  if (showTotalCost) {
    // Calculate number of columns to determine colspan for total
    let colCount = 0;
    if (showItem) colCount++;
    if (showQuantity) colCount++;
    if (showUnit) colCount++;
    if (showPrice) colCount++;
    
    // If no price column is shown, we'll still display the total in the last available column
    if (colCount === 0) colCount = 1; // Ensure at least one column
    
    if (showPrice) {
      // Normal case: show total in price column
      html += `
            <tr class="border-t-2 border-gray-300">
              ${showItem ? '<td class="py-5 font-bold text-base">Total</td>' : ''}
              ${showQuantity ? '<td></td>' : ''}
              ${showUnit ? '<td></td>' : ''}
              <td class="text-right py-5 font-bold text-base">$${formatCurrency(grandTotal)}</td>
            </tr>
      `;
    } else if (showItem) {
      // Special case: no price column but showTotalCost is true - span across all columns
      html += `
            <tr class="border-t-2 border-gray-300">
              <td class="py-5 font-bold text-base" colspan="${colCount}">
                <div class="flex justify-between">
                  <span>Total</span>
                  <span>$${formatCurrency(grandTotal)}</span>
                </div>
              </td>
            </tr>
      `;
    } else {
      // Edge case: show total in single spanning cell
      html += `
            <tr class="border-t-2 border-gray-300">
              <td class="py-5 font-bold text-base text-right" colspan="${colCount}">
                Total: $${formatCurrency(grandTotal)}
              </td>
            </tr>
      `;
    }
  }
  
  html += `
        </tbody>
      </table>
    </div>
  `;

  return html;
};

// Full Topsail Agreement (13 pages + ESTIMATE cover) — strict PDF-faithful layout.
// - Page 0 = ESTIMATE cover (Topsail brand palette, Grunce Coasten display font,
//   Avenir Next body), auto-populated from the worksheet's scope items + total.
// - All signature/date/initials fields are PLAIN underlines EXCEPT the Customer
//   Signature/Date/Customer Name on Page 2 which are the canvas injection targets.
// - Fillable text/table cells carry `data-agreement-field=...` /
//   `data-agreement-cost="row.col"` attributes so `injectAgreementDetails()`
//   can populate them from `proposal.agreementDetails`.
// - Cost items / cost groups / pricing render on the ESTIMATE cover only
//   (the old Page 9 SCOPE-of-WORK section has been removed).
const DEFAULT_PROPOSAL_TEMPLATE = `
<style>
  .tp-agreement { color: #111827; font-family: 'Avenir Next', 'Avenir', Arial, Helvetica, sans-serif; }
  .tp-agreement p, .tp-agreement li { line-height: 1.5; }
  .tp-agreement h1, .tp-agreement h2, .tp-agreement h3, .tp-agreement h4 { color: #111827; }
  .tp-page { padding: 12px 0 32px 0; }
  .tp-page + .tp-page { border-top: 1px dashed #e5e7eb; margin-top: 24px; }

  /* --- ESTIMATE cover page (before Page 1 "AGREEMENT") ------------------ */
  .tp-estimate-cover {
    background: #f6efd8;
    color: #1e3a5f;
    padding: 0 !important;
    margin: 0 0 24px 0;
    font-family: 'Avenir Next', 'Avenir', Arial, Helvetica, sans-serif;
    /* Force a page break AFTER the cover so the agreement starts fresh,
       but allow the cover itself to flow across multiple pages when the
       scope list is long. Without page-break-inside:auto some print
       engines will treat the whole section as a single page and cut
       content off at the bottom. */
    page-break-after: always;
    break-after: page;
    page-break-inside: auto;
    break-inside: auto;
    /* Never cap the height — the cream background must always reach the
       end of the content so bullets and the COST row don't hang off the
       edge of the coloured block. */
    min-height: 0;
    max-height: none;
    overflow: visible;
  }
  .tp-estimate-cover + .tp-page { border-top: none; margin-top: 0; }
  .tp-estimate-banner {
    background: #1e3a5f;
    color: #f6efd8;
    padding: 22px 32px 8px 32px;
    display: flex;
    align-items: center;
    gap: 24px;
  }
  .tp-estimate-banner-logo img { height: 110px; width: auto; display: block; }
  .tp-estimate-banner-center { flex: 1; text-align: center; }
  .tp-estimate-title {
    font-family: 'Grunce Coasten', 'Avenir Next', 'Avenir', Georgia, serif;
    font-size: 52px;
    letter-spacing: 3px;
    color: #f6efd8;
    line-height: 1;
    margin: 0;
  }
  .tp-estimate-banner-meta {
    flex: 0 0 auto;
    text-align: right;
    font-size: 12px;
    line-height: 1.55;
    color: #f6efd8;
  }
  .tp-estimate-banner-meta em { font-style: italic; }
  .tp-estimate-banner-date {
    background: #1e3a5f;
    color: #f6efd8;
    text-align: right;
    padding: 4px 32px 18px 32px;
    font-size: 14px;
  }
  .tp-estimate-body { padding: 26px 40px 40px 40px; }
  .tp-estimate-client-block { margin-bottom: 18px; line-height: 1.65; font-size: 14px; }
  .tp-estimate-client-block em { font-style: italic; font-weight: 600; }
  .tp-estimate-site-block { margin-bottom: 18px; font-size: 14px; }
  .tp-estimate-site-block em { font-style: italic; font-weight: 600; }
  .tp-estimate-intro { text-decoration: underline; margin: 18px 0 14px 0; font-weight: 400; font-size: 14px; }
  .tp-estimate-scope-title {
    font-style: italic;
    font-weight: 700;
    text-decoration: underline;
    margin: 14px 0 10px 0;
    font-size: 14px;
  }
  /* Collapse the outer scope title when we render per-group titles inside
     estimateScopeHtml (buildEstimateScope leaves it empty in that case so
     the group headings become the visible titles). */
  .tp-estimate-scope-title:empty { display: none; }
  /* Preserve author-entered line breaks in group descriptions so the
     "prose" the estimator typed doesn't turn into a single wall of run-on
     text on the estimate cover. */
  .tp-estimate-scope-lead { margin: 6px 0 10px 0; font-size: 14px; white-space: pre-line; }
  .tp-estimate-scope-list { list-style-type: disc; padding-left: 22px; margin: 6px 0 12px 0; }
  .tp-estimate-scope-list li { margin: 5px 0; font-size: 14px; line-height: 1.5; }
  /* Optional quantity / unit / price appendix that follows a cost-code
     bullet when the corresponding "Show Quantities / Units / Pricing"
     display setting is turned on. */
  /* Optional quantity/unit/price appendix that follows a cost-code bullet
     when the corresponding "Show Quantities/Units/Pricing" display setting
     is on. Kept slightly muted so the item name stays the primary read. */
  .tp-estimate-item-meta {
    font-weight: 500;
    color: #33507a;
    margin-left: 6px;
    white-space: nowrap;
  }
  /* Sub-description printed underneath a cost-code bullet when
     "Cost Code Descriptions" is on. Italic + slightly indented so it
     reads as a note without competing with the bullet label. */
  .tp-estimate-item-desc {
    font-size: 13px;
    margin: 3px 0 4px 0;
    padding-left: 2px;
    white-space: pre-line;
    color: #4a5568;
    font-style: italic;
  }
  /* Photos attached to scope items on the estimate cover page. Kept as a
     responsive tile grid so long galleries flow naturally across pages. */
  .tp-estimate-photos-block { margin: 18px 0 8px 0; page-break-inside: auto; break-inside: auto; }
  .tp-estimate-photo-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 8px;
  }
  .tp-estimate-photo {
    margin: 0;
    border: 1px solid #d4c8a1;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;
  }
  .tp-estimate-photo img {
    width: 100%;
    height: 140px;
    object-fit: cover;
    display: block;
  }
  .tp-estimate-photo figcaption {
    font-size: 11px;
    padding: 4px 6px;
    color: #4b5563;
    background: #f6efd8;
    border-top: 1px solid #d4c8a1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tp-estimate-cost-row {
    display: flex;
    justify-content: flex-end;
    align-items: baseline;
    gap: 18px;
    margin: 34px 0 12px 0;
  }
  .tp-estimate-cost-label {
    font-family: 'Grunce Coasten', 'Avenir Next', 'Avenir', Georgia, serif;
    font-size: 40px;
    letter-spacing: 2px;
    color: #1e3a5f;
  }
  .tp-estimate-cost-value { font-size: 22px; font-weight: 700; color: #1e3a5f; }
  .tp-estimate-submitted {
    border-top: 1px solid #1e3a5f;
    padding-top: 10px;
    margin-top: 22px;
    font-size: 13px;
  }
  .tp-estimate-submitted em { font-style: italic; font-weight: 600; text-decoration: underline; }

  .tp-fill {
    display: inline-block;
    border-bottom: 1px solid #111827;
    min-width: 180px;
    padding: 0 4px;
    line-height: 1.4;
    vertical-align: baseline;
  }
  .tp-fill-xs { min-width: 60px; }
  .tp-fill-sm { min-width: 100px; }
  .tp-fill-md { min-width: 180px; }
  .tp-fill-lg { min-width: 260px; }
  .tp-fill-xl { min-width: 340px; }
  .tp-fill-block { display: block; width: 100%; }
  .tp-info-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
  }
  .tp-info-table td {
    border: 1px solid #111827;
    padding: 8px 10px;
    vertical-align: top;
    width: 50%;
  }
  .tp-info-table .tp-info-head {
    font-weight: 700;
    padding-bottom: 6px;
    display: block;
  }
  .tp-info-table .tp-info-row {
    margin: 4px 0;
    line-height: 1.6;
  }
  .tp-payment-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  .tp-payment-table th, .tp-payment-table td {
    border: 1px solid #111827;
    padding: 10px 8px;
    vertical-align: middle;
    height: 34px;
  }
  .tp-payment-table th { text-align: center; text-decoration: underline; }
  .tp-payment-table .tp-cell-blank { min-height: 22px; }
  .tp-page-num { text-align: center; color: #4b5563; font-size: 12px; margin-top: 28px; }
  .tp-initials-row {
    display: flex;
    justify-content: space-between;
    gap: 32px;
    margin-top: 40px;
    font-size: 14px;
  }
  .tp-initials-row .tp-fill { min-width: 90px; }
  .tp-red { color: #dc2626; font-weight: 700; }
  .tp-italic { font-style: italic; }
  .tp-signature-line { display: flex; align-items: baseline; gap: 12px; margin-top: 18px; flex-wrap: wrap; }
  .tp-signature-line .tp-label { font-weight: 400; white-space: nowrap; }
  .tp-signable {
    display: inline-block;
    border-bottom: 1px solid #111827;
    min-width: 280px;
    min-height: 32px;
    padding: 0 4px;
    vertical-align: baseline;
    line-height: 1.4;
  }
  .tp-signable img { max-height: 40px; display: block; }
  @media print {
    .tp-page { page-break-after: always; break-after: page; }
    .tp-page:last-of-type { page-break-after: auto; break-after: auto; }
    .tp-page + .tp-page { border-top: none; margin-top: 0; }
  }
</style>

<div class="p-0 tp-agreement">

  <!-- ============================= COVER: ESTIMATE ============================= -->
  <section class="tp-estimate-cover">
    <div class="tp-estimate-banner">
      <div class="tp-estimate-banner-logo">
        <!-- Circular rope crest — served from the public folder so the
             signaturesSlice logo-URL rewrite (which rewrites any
             pub-*.r2.dev/...logo... to the horizontal wordmark) leaves
             this alone. -->
        <img src="/logo/topsail-crest.png" alt="Topsail Roofing + Construction" />
      </div>
      <div class="tp-estimate-banner-center">
        <div class="tp-estimate-title">ESTIMATE</div>
      </div>
      <div class="tp-estimate-banner-meta">
        <div>P.O. Box 3427</div>
        <div>San Clemente, CA 92674</div>
        <div><em>Phone:</em> (949) 370-8612</div>
        <div><em>License#</em> 1145393</div>
        <div>www.topsailroofing.com</div>
      </div>
    </div>
    <div class="tp-estimate-banner-date">Date: \${today}</div>
    <div class="tp-estimate-body">
      <div class="tp-estimate-client-block">
        <div><em>Client:</em> \${lead.clientName}</div>
        <div>\${lead.streetAddress}\${lead.cityLine}</div>
        <div>\${lead.clientPhone}</div>
        <div>\${lead.clientEmail}</div>
      </div>
      <div class="tp-estimate-site-block">
        <div><em>Site Address:</em> \${lead.streetAddress}\${lead.cityLine}</div>
      </div>

      <p class="tp-estimate-intro">Topsail Roofing &amp; Construction agrees to provide all labor and material necessary to complete the following scope of work;</p>

      <div class="tp-estimate-scope-title">\${estimateScopeTitle}</div>
      \${estimateScopeHtml}

      \${estimatePhotosHtml}

      \${estimateCostRow}

      <p class="tp-estimate-submitted"><em>SUBMITTED BY:</em> \${submittedByLine}</p>
    </div>
  </section>

  <!-- The Topsail wordmark used to sit above the AGREEMENT heading; per
       client request it's been removed so the branded ESTIMATE cover is
       the only logo the reader sees at the top of the document. -->

  <!-- ============================= PAGE 1 ============================= -->
  <section class="tp-page">
    <h1 class="text-3xl font-bold text-center mb-8">AGREEMENT</h1>

    <table class="tp-info-table">
      <tbody>
        <tr>
          <td>
            <span class="tp-info-head">Contractor Information</span>
            <div class="tp-info-row">Topsail Roofing and Construction Inc.</div>
            <div class="tp-info-row">License #: 1145393</div>
            <div class="tp-info-row">PO Box 3427, San Clemente CA 92674</div>
            <div class="tp-info-row">949-370-8612</div>
            <div class="tp-info-row">Fletcher@topsailroofing.com</div>
          </td>
          <td>
            <span class="tp-info-head">Customer Information</span>
            <div class="tp-info-row">Name: \${lead.clientName}</div>
            <div class="tp-info-row">Property Address: \${lead.streetAddress}</div>
            <div class="tp-info-row">&nbsp;</div>
            <div class="tp-info-row">Email: \${lead.clientEmail}</div>
            <div class="tp-info-row">Phone: \${lead.clientPhone}</div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="mt-6 space-y-2 text-base">
      <div>Proposal Date: \${today}</div>
      <div>&nbsp;</div>
      <div>Approximate Start Date: <span class="tp-fill tp-fill-md" data-agreement-field="approxStartDate">\${agreementDetails.approxStartDate}</span></div>
      <div>Approximate Completion Date: <span class="tp-fill tp-fill-md" data-agreement-field="approxCompletionDate">\${agreementDetails.approxCompletionDate}</span></div>
      <div>Approximate Duration: <span class="tp-fill tp-fill-md" data-agreement-field="approxDuration">\${agreementDetails.approxDuration}</span></div>
      <div>Project Number: \${lead.jobNumber}</div>
    </div>

    <div class="mt-8 space-y-3">
      <p>This Agreement (or Contract) outlines the work we will perform and the payment you will make. It includes all attached documents and is effective once both parties sign below.</p>
      <p>Until this Agreement is accepted by you, it is considered only a proposal. If you don't accept it within 30 days of the Proposal Date above, it is not binding.</p>
      <p>The exact product sizes and specifications will be finalized and approved by the Customer at a job preview meeting.</p>
      <p>Any subsequent modifications or agreements signed or initialed by both you and us will be considered a part of this Agreement.</p>
      <p>This Agreement is governed by the laws of the State of California. Customer has the right to require Topsail to have a performance and payment bond, at the customer's cost.</p>
    </div>

    <div class="mt-6">
      <p class="font-bold">CANCELLATION NOTICE:</p>
      <p class="font-bold">YOU, THE BUYER, MAY CANCEL THIS TRANSACTION AT ANY TIME PRIOR TO MIDNIGHT OF THE THIRD BUSINESS DAY AFTER THE DATE OF THIS TRANSACTION.<span class="font-normal"> You may cancel by</span></p>
    </div>

    <div class="tp-page-num">-- 1 of 14 --</div>
  </section>

  <!-- ============================= PAGE 2 ============================= -->
  <section class="tp-page">
    <p>emailing, mailing, faxing, or delivering a written notice to us at the address/email provided above. Call us at 949-345-5574 to help with the cancellation process.</p>

    <p class="font-bold mt-8">ACCEPTANCE:</p>
    <p>We have both read, understood, and agreed to this Agreement and its attachments.</p>

    <!-- Customer signable fields: data-sig-role marks the slots the signature/date/name are injected into. -->
    <div class="tp-signature-line">
      <span class="tp-label">Customer Signature:</span>
      <div class="border-b border-gray-300 pb-2 tp-signable" style="display:inline-block; min-width:280px;" data-sig-role="signature">
        <div class="h-8"></div>
      </div>
      <span class="tp-label">Date:</span>
      <div class="border-b border-gray-300 pb-2 tp-signable" style="display:inline-block; min-width:150px;" data-sig-role="date">
        <div class="h-6"></div>
      </div>
    </div>
    <div class="tp-signature-line">
      <span class="tp-label">Customer Name:</span>
      <div class="border-b border-gray-300 pb-2 tp-signable" style="display:inline-block; min-width:340px;" data-sig-role="name">
        <div class="h-6"></div>
      </div>
    </div>

    <div class="tp-signature-line">
      <span class="tp-label">Contractor Signature:</span>
      <span class="tp-fill tp-fill-lg" data-contractor-signature="true">\${contractorSignatureImg}</span>
      <span class="tp-label">Date:</span>
      <span class="tp-fill tp-fill-sm" data-contractor-date="true">\${contractorDate}</span>
    </div>
    <div class="tp-signature-line">
      <span class="tp-label">Contractor Name/Title:</span>
      <span class="tp-fill tp-fill-xl" data-contractor-name="true">\${contractorName}</span>
    </div>

    <p class="mt-8 tp-italic">The Customer is entitled to a completed and signed copy of this Agreement before any work may be started.</p>

    <div class="tp-page-num">-- 2 of 14 --</div>
  </section>

  <!-- ============================= PAGE 3 ============================= -->
  <section class="tp-page">
    <h2 class="text-lg font-bold">CONTRACT PRICE &amp; SCHEDULE OF PAYMENTS</h2>
    <p class="mt-4 tp-italic">NOTE: IT IS AGAINST THE LAW FOR A CONTRACTOR TO COLLECT PAYMENT FOR WORK NOT YET COMPLETED, OR FOR MATERIALS NOT YET DELIVERED. HOWEVER, A CONTRACTOR MAY REQUIRE A DOWN PAYMENT.</p>

    <p class="mt-6 font-bold">Schedule:</p>
    <ul class="list-disc pl-6 space-y-2 mt-2">
      <li>Down Payment to be paid when signing this Agreement. <span class="tp-italic">(MAY NOT EXCEED $1,000 OR 10 PERCENT OF THE CONTRACT PRICE, WHICHEVER IS LESS)</span></li>
      <li>Mobilization Payment to be paid upon project mobilization.</li>
      <li>Progress Payment <span class="tp-italic">to be paid upon completion of:</span><br /><span class="tp-fill tp-fill-block" style="min-height:22px" data-agreement-field="progressPaymentUpon">\${agreementDetails.progressPaymentUpon}</span></li>
      <li>Balance to be paid at completion.</li>
    </ul>

    <p class="mt-6 font-bold">Other Costs:</p>
    <ul class="list-disc pl-6 space-y-2 mt-2">
      <li>The Building Permit fee is to be determined by local Building Department.</li>
      <li>The Building Permit Service Fee is TBD, and to be paid to Topsail when the permit is issued.</li>
    </ul>

    <p class="mt-6 font-bold">Agreement Costs:</p>
    <table class="tp-payment-table">
      <thead>
        <tr>
          <th></th>
          <th>Amount</th>
          <th>Date Paid</th>
          <th>Check #</th>
          <th>Initialed</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>Down Payment</td><td data-agreement-cost="downPayment.amount"></td><td data-agreement-cost="downPayment.datePaid"></td><td data-agreement-cost="downPayment.checkNumber"></td><td data-agreement-cost="downPayment.initialed"></td></tr>
        <tr><td>Mobilization Payment</td><td data-agreement-cost="mobilization.amount"></td><td data-agreement-cost="mobilization.datePaid"></td><td data-agreement-cost="mobilization.checkNumber"></td><td data-agreement-cost="mobilization.initialed"></td></tr>
        <tr><td>Progress Payment</td><td data-agreement-cost="progressPayment.amount"></td><td data-agreement-cost="progressPayment.datePaid"></td><td data-agreement-cost="progressPayment.checkNumber"></td><td data-agreement-cost="progressPayment.initialed"></td></tr>
        <tr><td>Balance at Completion</td><td data-agreement-cost="balance.amount"></td><td data-agreement-cost="balance.datePaid"></td><td data-agreement-cost="balance.checkNumber"></td><td data-agreement-cost="balance.initialed"></td></tr>
        <tr><td class="font-bold">TOTAL</td><td data-agreement-cost="total.amount"></td><td data-agreement-cost="total.datePaid"></td><td data-agreement-cost="total.checkNumber"></td><td data-agreement-cost="total.initialed"></td></tr>
      </tbody>
    </table>

    <table class="tp-payment-table" style="margin-top:16px; table-layout: fixed;">
      <colgroup>
        <col style="width: 42%;" />
        <col style="width: 22%;" />
        <col style="width: 18%;" />
        <col style="width: 9%;" />
        <col style="width: 9%;" />
      </colgroup>
      <tbody>
        <tr><td style="white-space: nowrap;">Building Permit TBD</td><td data-agreement-cost="buildingPermit.amount">Included</td><td data-agreement-cost="buildingPermit.datePaid"></td><td data-agreement-cost="buildingPermit.checkNumber"></td><td data-agreement-cost="buildingPermit.initialed"></td></tr>
        <tr><td style="white-space: nowrap;">Permitting Service TBD</td><td data-agreement-cost="permittingService.amount">Included</td><td data-agreement-cost="permittingService.datePaid"></td><td data-agreement-cost="permittingService.checkNumber"></td><td data-agreement-cost="permittingService.initialed"></td></tr>
      </tbody>
    </table>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 3 of 14 --</div>
  </section>

  <!-- ============================= PAGES 4-7: TERMS ============================= -->
  <section class="tp-page">
    <h2 class="text-lg font-bold">AGREEMENT TERMS and CONDITIONS</h2>

    <h3 class="font-bold mt-6">1. TIME FOR PERFORMANCE</h3>
    <p>Work will start no later than the date on page one, and be completed on or before the date on page one.</p>
    <p>Materials arriving at the job site is considered the commencement of the work.</p>
    <p>Failure by Topsail without lawful excuse to substantially commence work within 20 days from the approximate date specified in the contract when work will begin is a violation of Contractors' State License Law.</p>
    <p>Topsail can make cost and schedule adjustments if work is effected by circumstances beyond its reasonable control, such as natural elements (including inclement weather), fire, strikes and supply chain disruptions, pandemics/epidemics, governmental acts, and acts or omissions of other persons (including other contractors), and unforeseen or unknown pre-existing conditions.</p>
    <p>If work does not start within 45 days of the commencement date or if work is not completed within 45 days of the completion date due to delay by the Customer for any reason, Topsail may, at its sole discretion, increase the total contract price or declare the Agreement "Canceled by Customer" pursuant to Paragraph 14 of these Terms and Conditions, below.</p>
    <p>Customer hereby authorizes Topsail to order material, fabricate parts, contract for labor in accordance with the scope of work. All payments made prior to commencement of the work at the jobsite will be considered as partial payment for all such materials.</p>
    <p>Customer acknowledges that Topsail is not responsible for pre-existing conditions of home, grounds, or existing structures, such as uneven or irregular walls, foundations or floors, dry rot or termite damage, or any other conditions that may ultimately affect Topsail's performance of the work.</p>

    <h3 class="font-bold mt-6">2. EXTRA WORK AND CHANGE ORDERS</h3>
    <p>Any changes or additions to the work covered in this Agreement (other than as a result of delay or destruction) shall be in writing signed by both parties and shall include the agreed adjustments to the cost and schedule of the work. Extra Work and Change Orders become part of the Agreement and only if the order is prepared in writing and signed by the parties prior to the commencement of any work covered by the new Change Order. The Change Order must describe the scope of the extra work or change, the cost to be added or subtracted from the Contract, and the effect the order will have on the schedule of progress payments. The cost of such changes shall include Topsail's costs for materials, labor, overhead and profit. However, Topsail's failure to comply with this provision does not preclude the recovery of compensation for work performed based upon legal or equitable remedies designed to prevent unjust enrichment.</p>

    <h3 class="font-bold mt-6">3. COMPLETION OF JOB</h3>
    <p>Upon satisfactory payment being made for any portion of the work performed and written request of the Customer within ten days of such payment, Topsail shall, prior to any further payment being made, furnish to the person contracting for the home improvement an unconditional release from any claim or mechanic's lien pursuant to Civil Code section 8134 or 8138, as applicable, in the form provided at the link below or equivalent, for that portion of the work for which payment has been made.</p>
    <p>https://cslb.ca.gov/consumers/legal_issues_for_consumers/mechanics_lien/<br/>Conditional_And_Unconditional_Waiver_Release_Form.aspx</p>
    <p>This Agreement is based on a completed job, and any surplus material remains the property of Topsail. No credit is due Customer on return of such materials.</p>
    <p>"Completion" for those projects requiring a building permit when the project receives final inspection by the permitting authority. For repairs or projects not requiring a building permit, "Completion" is achieved when Topsail demobilizes from the job site.</p>
    <p class="tp-red">A "PUNCH LIST" DOES NOT DEFER THE CUSTOMER'S OBLIGATION TO PAY TOPSAIL ACCORDING TO THE TERMS OF THIS AGREEMENT.</p>

    <div class="tp-page-num">-- 4 of 14 --</div>
  </section>

  <section class="tp-page">
    <h3 class="font-bold">4. CUSTOMER'S PROPERTY</h3>
    <p>Topsail will make protective arrangements so as to prevent work operations from damaging the premises, or other work or operations. Customer agrees to remove or to protect all personal property, inside or outside of the structure, including, but not limited to, rooftop-mounted equipment, carpets, rugs, curtains, drapes, shutters, wall hangings, mirrors, artwork, furniture, shrubs and plantings, and Topsail shall not be held liable for damage or loss for said items, howsoever caused. Further, Topsail is not liable for any damage to the Customer's real property or existing structures such as, but not limited to, nail pops, plaster or stucco cracks, or water damage, howsoever caused.</p>

    <h3 class="font-bold mt-6">5. CLEANUP</h3>
    <p>Topsail will promptly remove all equipment and materials on completion of the work and payment, and will leave the premises free from all refuse.</p>

    <h3 class="font-bold mt-6">6. PERMITS</h3>
    <p>Unless otherwise agreed, Customer will obtain all necessary licenses and permits required for all or any portion of the work to be performed under this Agreement. If it is agreed that Topsail will obtain Building Permits, Customer is responsible for reimbursing Topsail according to the Payment Schedule.</p>

    <h3 class="font-bold mt-6">7. INSURANCE</h3>
    <p>While work is being performed under this Agreement, Topsail will continuously provide Workers Compensation coverage and Commercial General Liability Insurance to protect against any results of Topsail's negligence.</p>

    <h3 class="font-bold mt-6">8. SUBROGATION WAIVER</h3>
    <p>Customer and Topsail hereby grant to each other, on behalf of any insurer providing insurance to either of them with respect to the premises, a waiver of any right of subrogation that any such insurer of one party may acquire against the other by virtue of payment of any loss under that insurance.</p>

    <h3 class="font-bold mt-6">9. ARBITRATION OF DISPUTES</h3>
    <p>Any dispute regarding performance under this Agreement shall be raised and settled in an arbitration proceeding held in accordance with the Construction Industry Rules of the American Arbitration Association then in effect.</p>
    <p class="font-bold mt-3">"NOTICE: BY INITIALING IN THE SPACE BELOW YOU ARE AGREEING TO HAVE ANY DISPUTE ARISING OUT OF THE MATTERS INCLUDED IN THE "ARBITRATION OF DISPUTES" PROVISION DECIDED BY NEUTRAL ARBITRATION AS PROVIDED BY CALIFORNIA LAW AND YOU ARE GIVING UP ANY RIGHTS YOU MIGHT POSSESS TO HAVE THE DISPUTE LITIGATED IN A COURT OR JURY TRIAL. BY INITIALING IN THE SPACE BELOW YOU ARE GIVING UP YOUR JUDICIAL RIGHTS TO DISCOVERY AND APPEAL, UNLESS THOSE RIGHTS ARE SPECIFICALLY INCLUDED IN THE "ARBITRATION OF DISPUTES" PROVISION. IF YOU REFUSE TO SUBMIT TO ARBITRATION AFTER AGREEING TO THIS PROVISION, YOU MAY BE COMPELLED TO ARBITRATE UNDER THE AUTHORITY OF THE BUSINESS AND PROFESSIONS CODE OR OTHER APPLICABLE LAWS. YOUR AGREEMENT TO THIS ARBITRATION PROVISION IS VOLUNTARY.</p>
    <p class="font-bold mt-3">"WE HAVE READ AND UNDERSTAND THE FOREGOING AND AGREE TO SUBMIT DISPUTES ARISING OUT OF THE MATTERS INCLUDED IN THE 'ARBITRATION OF DISPUTES' PROVISION TO NEUTRAL ARBITRATION."</p>

    <h3 class="font-bold mt-6">10. ATTORNEY'S FEES AND COSTS</h3>

    <div class="tp-page-num">-- 5 of 14 --</div>
  </section>

  <section class="tp-page">
    <p>The Customer shall pay all reasonable and actual costs and attorneys' fees occasioned by any default in making payments hereunder. In the event that any action is brought to enforce this Agreement, the prevailing party shall be entitled to an award of reasonable attorneys' fees incurred in prosecuting or defending the proceeding.</p>

    <h3 class="font-bold mt-6">11. DEFAULT</h3>
    <p>If Customer fails to compensate Topsail according to the terms of this Agreement for a period of 60 (sixty) days after compensation is due, Topsail, in addition to any other legal remedies or processes available, may terminate the Agreement and enter the premises and remove any portion of, or all, materials or goods installed at or affixed to the premises under this Agreement, to the extent that they represent in their then current condition the portion of the price then owing to Topsail. However, the work of removal shall not substantially interfere with any materials installed outside of this agreement.</p>

    <h3 class="font-bold mt-6">12. WARRANTY</h3>
    <p>Unless otherwise noted in this document, the Topsail warrants all their roof systems to be free from failures (leaks) due to faulty applications (workmanship). If a failure (leak) does occur due to faulty application, Topsail will be responsible to repair the damage and the roof at their cost if it occurs during the warranty period and if it is roof related.</p>
    <p>Failures from existing skylights (new or old), rain gutters, or from other trades, and all devices attached to the roof, are excluded from this warranty.</p>
    <p>The labor warranty period for all flat-roof (BUR) applications is five years from the installation date unless otherwise stated in the contract.</p>
    <p>The labor warranty period for all pitched-roof (composition/tile) applications is seven years unless otherwise stated in the Agreement.</p>
    <p>In the event Topsail is called out to repair a problem or a leak which is determined to be caused by something other than "craftsmanship", ie wind, hail, lightning, fires, or acts of God, intentional or accidental damage, vandalism, or any other causes beyond reasonable control of Topsail, or in areas not affected by companies work, pre existing conditions etc. , all costs incurred by Topsail shall be reimbursed by Customer in accordance with then prevailing rates.</p>
    <p>Topsail shall in no way be held liable or responsible for consequential or incidental damages of any kind to the outside or inside of the building.</p>
    <p>Lack of owner maintenance will void any warranty.</p>
    <p class="tp-red">Topsail does not provide any warranty for roof leak repairs.</p>

    <h3 class="font-bold mt-6">13. WARRANTY TRANSFERABILITY</h3>
    <p>This limited warranty may be transferred to the first subsequent owner of the property by the original Customer, provided Topsail receives written notice together with $150.00 within 60 days after the date of transfer of ownership of the property. Failure to do so shall result in termination of any obligations on the part of Topsail with respect to this limited warranty.</p>

    <h3 class="font-bold mt-6">14. CANCELLATION</h3>
    <p>In the event of any termination or cancellation of this Agreement by the Customer after the three-day cancellation period, Customer agrees to pay Topsail a cancellation charge for breach of contract in the amount of 30% of the contract price plus the actual cost of labor and materials incurred up to and including the date official written notice of termination is received by Topsail, plus any costs of demobilization. Topsail may cancel this Agreement at any time prior to commencement of work as defined in Paragraph 1.</p>

    <h3 class="font-bold mt-6">15. EXCLUSIONS</h3>
    <p>Topsail is not responsible for ponding water on flat roof or deck systems.</p>
    <p>Roof work may cause movement of building structure that results in cracks in interior drywall/plaster.</p>

    <div class="tp-page-num">-- 6 of 14 --</div>
  </section>

  <section class="tp-page">
    <p>Topsail is not responsible for drywall/plaster cracks.</p>
    <p>It is the owner's responsibility to cover or remove personal property from work areas including attic space, garage, decks, patios, and areas around the roof. Topsail is not responsible for dust or debris that falls into attic or garage space.</p>
    <p>Topsail is not responsible for damage to or arising from plumbing lines, pipes, drains, HVAC lines or other components that are installed within nailing distance of roof/deck substrate or structural members.</p>
    <p>Topsail shall not be responsible for any liability or damages caused or alleged to be caused by mold fungi, spores, bacteria or other such agents that arise from any source whatsoever. Such damages include, but are not limited to property damage, personal injury, loss of income, emotional distress, death, loss of use, loss of value, adverse health effects or other damages.</p>
    <p>Customer may at Customer's expense test for asbestos prior to job start. Topsail does not supply or handle asbestos-related products. Any asbestos in the structure is pre-existing and Owner agrees to hold Contractor harmless from and against any claims, damages, losses, liabilities costs or expenses (including reasonable legal fees and costs) arising from the presence of asbestos on site.</p>
    <p>Customer agrees to approve roof removal date in accordance with a favorable weather forecast and Topsail shall not be held liable for any moisture damages arising from such Customer-approved open roof for a period of forty-eight (48) hours following approval or as otherwise agreed in advance in writing.</p>

    <h3 class="font-bold mt-6">16. EXCLUSIVE REMEDIES</h3>
    <p>Customer's sole and exclusive remedy against Topsail for any and all claims for loss or damage of any nature arising out of, or alleged to have arisen out of, this Agreement or Topsail's work shall be as set forth in this Agreement, whether or not such loss or damage is based on contract, warranty, negligence or any other legal or equitable theory and Customer shall release, defend and indemnify Topsail from and against all further liability. Topsail's maximum aggregate liability under this Agreement shall not under any circumstances exceed 10% of the contract price of this Agreement.</p>

    <h3 class="font-bold mt-6">17. LATE FEES</h3>
    <p>Invoices are due upon receipt. There will be a 1.5% interest charge per month after the first 30 days.</p>

    <h3 class="font-bold mt-6">18. SUBCONTRACTORS</h3>
    <p>We may use one or more subcontractors for this project. We will provide, upon request, a list of subcontractors with their contact information, license number, and classification.</p>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 7 of 14 --</div>
  </section>

  <!-- ============================= PAGE 8: UNEXPECTED CONDITIONS ============================= -->
  <section class="tp-page">
    <h2 class="text-lg font-bold">UNEXPECTED CONDITIONS and HIDDEN DAMAGE POLICY</h2>
    <p class="mt-4">Occasionally during the construction process, we may encounter unexpected conditions that could not have reasonably been anticipated prior to the commencement of construction. These conditions could include but are not limited to irregularities in the existing construction, dry rot, termite damage, settling or foundation movement, decaying siding, crumbling stucco, asbestos, or internal obstacles such as plumbing or electrical wires. Occasionally, building inspectors will require changes to be made that were not anticipated in the project plan and scope of work. In such cases, it may be necessary to correct these conditions before the project can be completed. This Agreement cannot anticipate the time nor cost necessary to correct unexpected conditions. In such an event, Topsail's policy is as follows:</p>

    <ol class="list-decimal pl-6 space-y-3 mt-4">
      <li>If the repairs or modifications are required to meet code or industry standards (including but not limited to building inspector requirements, dry rot, and termite damage), and can be made by Topsail, the extra work will be done without additional authorization from the Customer, at an additional cost, on a "time and materials" basis. Time and materials rates are as set forth in the Schedule of Prevailing Rates.</li>
      <li>If repairs or modifications to correct these unexpected conditions other than those set forth in Paragraph 1 above can be made by Topsail, Topsail will advise the Customer, and upon the customer's approval the extra work will be done at an additional cost, on a "time and materials" basis, or if the scope can be clearly defined, on a mutually agreed fixed price. Time and materials rates are as set forth in the attached Schedule of Prevailing Rates; or</li>
      <li>If Topsail is unable to make the repairs or modifications, Topsail will advise the Customer, and upon the customer's approval will contract with the appropriate specialty service provider to have the work done. Topsail will be reimbursed by Customer in accordance with the attached Schedule of Prevailing Rates; or</li>
      <li>The customer may make the repairs or modifications himself, or hire his own contractor at his expense; or</li>
      <li>If it proves unfeasible or too costly to make the repairs or modifications, the Customer agrees to be responsible for the cost to put that portion of the project back to its original condition and to reimburse Topsail for the product that was ordered for this location even though it cannot be installed.</li>
      <li>The project schedule shall be adjusted for the extra work.</li>
      <li>Ponding water is inherent on a flat roof and deck. Even with tapered systems, 100% removal is beyond our scope of work.</li>
      <li>Agreement includes one layer of roof material. Extra layers required will be an extra charge. Owner agrees to pay contractor to replace bad wood at current market value for fascia, starter board, and plywood. Shop rate for other approved repairs is $94 per man hour.</li>
    </ol>

    <p class="mt-6 tp-italic">I have read and understood the above policy, and agree to its terms.</p>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 8 of 14 --</div>
  </section>

  <!-- Page 9 (SCOPE of WORK) was moved to the ESTIMATE cover page above; -->
  <!-- the itemsHtml block is now rendered there instead of duplicated here. -->

  <!-- ============================= PAGE 10: PREPARING ============================= -->
  <section class="tp-page">
    <h2 class="text-lg font-bold">Preparing For Your Roofing Project</h2>
    <p class="mt-4">Thank you for choosing Topsail Roofing Company! To keep any disruptions to a minimum, please take a moment to review the following information:</p>

    <ul class="list-disc pl-6 space-y-3 mt-4">
      <li>Please remove any/all vehicles from the driveway/garage before 7:00 am each day.</li>
      <li>Please remove, cover, or store any sensitive property around the outside of your house to prevent damage from dust and debris. This may include patio furniture, BBQ grills, rugs, potted plants, etc. (Topsail is not responsible for any damage)</li>
      <li>Your home will be a construction site for the duration of your project. Please note some debris may get overlooked such as a hidden nail or two in the bushes. Topsail does our best to provide daily clean-ups at the end of each workday. We use magnetic wheels to pick up nails/scrap metal.</li>
      <li>Some areas inside the home are more likely to experience falling dust and debris. These areas include but not limited to:
        <ul class="list-disc pl-6 mt-2 space-y-1">
          <li>Open beam ceilings (Tongue and groove or shiplap ceilings)</li>
          <li>Garages</li>
          <li>Attics</li>
          <li>Any area beneath a skylight (skylights will be removed and re-set throughout the project; debris and dust WILL fall inside the home)</li>
        </ul>
      </li>
      <li>Please prepare these areas by covering or removing sensitive property. (Topsail is not responsible for indoor/attic clean up)</li>
      <li>Roof work will cause vibrations that may cause the drywall/plaster to crack. (Topsail is not responsible for drywall/plaster)</li>
      <li>Working on the roof may cause vibrations that shake loose objects hanging on the walls. Please remove and store valuable objects to prevent damage. (Topsail is not responsible for damage of items)</li>
    </ul>

    <p class="mt-6">The Topsail crew will be happy to provide plastic covering and assist with anything you may need.</p>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 10 of 14 --</div>
  </section>

  <!-- ============================= PAGE 11: LEGAL NOTICES ============================= -->
  <section class="tp-page">
    <h2 class="text-lg font-bold">LEGAL NOTICES:</h2>

    <p class="font-bold mt-4">Contractors' State License Board (CSLB):</p>
    <p>The CSLB is the state consumer protection agency that licenses and regulates construction contractors. Contact the CSLB for information about the licensed contractor you are considering, including information about disclosable complaints, disciplinary actions, and civil judgments that are reported to the CSLB. For more information visit CSLB's web site at www.cslb.ca.gov, or call 800-321-2752</p>

    <p class="font-bold mt-4">MECHANICS' LIEN WARNING:</p>
    <p>Your property may be subject to a lien if you don't pay your contractor, or if your contractor fails to pay all parties who supplied materials or services.</p>
    <p>For ways to prevent liens, visit CSLB's Web site at www.cslb.ca.gov or call CSLB at 800-321-CSLB (2752).</p>

    <p class="font-bold mt-4">Three-Day Right to Cancel:</p>
    <p>You, the buyer, have the right to cancel this contract within three business days. You may cancel by e-mailing, mailing, faxing, or delivering a written notice to the contractor at the contractor's place of business by midnight of the third business day after you received a signed and dated copy of the contract that includes this notice. Include your name, your address, and the date you received the signed copy of the contract and this notice.</p>
    <p>If you cancel, the contractor must return to you anything you paid, within 10 days of receiving the notice of cancellation. You must make available to the contractor at the job location, in substantially as good condition as you received it, any goods delivered to you under this agreement. Or, you may, if you wish, comply with the contractor's instructions on how to return the goods at the contractor's expense and risk. If you do make the goods available to the contractor and the contractor does not pick them up within 20 days of the date of your notice of cancellation, you may keep them without any further obligation. If you fail to make the goods available to the contractor, or if you agree to return the goods to the contractor and fail to do so, then you remain liable for performance of all obligations under this contract.</p>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 11 of 14 --</div>
  </section>

  <!-- ============================= PAGE 12: SCHEDULE OF PREVAILING RATES ============================= -->
  <section class="tp-page">
    <h2 class="text-lg font-bold">SCHEDULE of PREVAILING RATES</h2>
    <p class="mt-4">The following Schedule of Prevailing Rates is divided into two separate sections: Contractor's Cost and Contractor's Fee. The Contractor's Cost section represents Topsail's total cost for 'Unit Rate' items, 'Time and Material' items, and Subcontracts, and is all-inclusive.</p>
    <p class="mt-3">Time and Material items are to be used for those items that are not listed under the Unit Rates classification. The Contractor's Fee section represents the markup to be applied to all Contractor's Costs. Actual Billing Rates to the customer are calculated as Contractor's Cost plus the Contractor's Fee.</p>

    <div class="mt-6">
      <p class="font-bold">A. CONTRACTOR'S COST</p>
      <p class="font-bold mt-3 ml-4">1. Unit Rates</p>
      <table class="w-full mt-2" style="border-collapse:collapse;">
        <thead>
          <tr>
            <th class="text-left pb-1" style="width:60%; padding-left:32px;">Item</th>
            <th class="text-left pb-1" style="width:40%;">Cost</th>
          </tr>
        </thead>
        <tbody class="align-top">
          <tr><td style="padding-left:32px;">• Stucco Repair (textured, paint-ready; $200.00 Minimum)</td><td>$55.00/sq ft</td></tr>
          <tr><td style="padding-left:32px;">• Stucco Repair (textured, color coat match; $300.00</td><td>$55.00/sq ft</td></tr>
          <tr><td style="padding-left:32px;">• Drywall Repair (textured, unprimed; $200.00 Minimum)</td><td>$16.00/sq ft</td></tr>
          <tr><td style="padding-left:32px;">• Metal Flashing Installation (galvanized or bonderized)<br /><span style="padding-left:16px;">Calculated as $1.75 x equivalent width of flashing in</span></td><td>$1.75/ft/width (inches)</td></tr>
          <tr>
            <td style="padding-left:32px;">• Roofing tile repair/replacement $15.00 per piece<br/><span style="padding-left:16px;">(most clay and concrete tiles)</span></td>
            <td>$15.00 per piece<br/>including labor &amp; material</td>
          </tr>
          <tr><td style="padding-left:32px;">• Wood Repair</td><td></td></tr>
          <tr><td style="padding-left:56px;">* Fascia</td><td>Current Market Rate</td></tr>
          <tr><td style="padding-left:56px;">* Starter board/shiplap</td><td>Current Market Rate</td></tr>
          <tr><td style="padding-left:56px;">* Plywood</td><td>Current Market Rate</td></tr>
          <tr><td style="padding-left:56px;">* Wood repair rates are subject to the following</td><td></td></tr>
          <tr>
            <td style="padding-left:80px;">- Ladder work, heights greater than ten feet,<br /><span style="padding-left:16px;">or roof slopes greater than 4/12:</span></td>
            <td>2.0 multiple</td>
          </tr>
          <tr>
            <td style="padding-left:80px;">- Heights greater than 20 feet, scabbing, sistering,<br /><span style="padding-left:16px;">penetrations, or roof slopes greater than 6/12:</span></td>
            <td>3.0 multiple</td>
          </tr>
        </tbody>
      </table>

      <p class="font-bold mt-4 ml-4">2. Time and Material Costs</p>
      <table class="w-full mt-2" style="border-collapse:collapse;">
        <tbody class="align-top">
          <tr><td style="padding-left:32px; width:60%;">• Labor</td><td style="width:40%;"></td></tr>
          <tr><td style="padding-left:56px;">* Superintendent</td><td>$104.00/hr</td></tr>
          <tr><td style="padding-left:56px;">* Shop Labor</td><td>$104.00/hr</td></tr>
          <tr><td style="padding-left:32px;">• Material Cost</td><td>(incl. Tax) + 15%</td></tr>
        </tbody>
      </table>

      <p class="font-bold mt-4 ml-4">3. Subcontracts <span class="font-normal float-right" style="min-width:200px;">Subcontracts Cost + 15%</span></p>
    </div>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 12 of 14 --</div>
  </section>

  <!-- ============================= PAGE 13: NOTICE OF RIGHT TO CANCEL ============================= -->
  <section class="tp-page">
    <div class="text-center font-bold">
      <div>NOTICE OF RIGHT TO CANCEL</div>
      <div>AND</div>
      <div>NOTICE OF CANCELATION</div>
    </div>

    <p class="mt-6">You may cancel this transaction, without any penalty or obligation, within three business days from the Agreement date.</p>
    <p class="mt-2">If you cancel, any property traded in, any payments made by you under the contract or sale, and any negotiable instrument executed by you will be returned within 10 days following receipt by the seller of your cancellation notice, and any security interest arising out of the transaction will be canceled.</p>
    <p class="mt-2">If you cancel, you must make available to the seller at your residence, in substantially as good condition as when received, any goods delivered to you under this contract or sale, or you may, if you wish, comply with the instructions of the seller regarding the return shipment of the goods at the seller's expense and risk.</p>
    <p class="mt-2">If you do make the goods available to the seller and the seller does not pick them up within 20 days of the date of your notice of cancellation, you may retain or dispose of the goods without any further obligation.</p>
    <p class="mt-2">If you fail to make the goods available to the seller, or if you agree to return the goods to the seller and fail to do so, then you remain liable for performance of all obligations under the contract.</p>
    <p class="mt-2">To cancel this transaction, mail, email, fax, or deliver a signed and dated copy of this cancellation notice, or any other written notice to:</p>

    <p class="font-bold mt-6">Cancellation by Buyer:</p>
    <p class="mt-2">Deliver to Topsail Roofing and Construction Inc.</p>
    <p>not later than midnight of <span class="tp-fill tp-fill-sm" data-agreement-field="cancellationDeadline">\${agreementDetails.cancellationDeadline}</span> (3 business days after date of transaction).</p>
    <p class="mt-2">PO Box 3427, San Clemente CA 92674</p>
    <p><a href="mailto:Fletcher@topsailroofing.com" class="text-blue-600 underline">Fletcher@topsailroofing.com</a></p>
    <p>Fax: 949-807-0122</p>

    <p class="mt-6 tp-italic">I hereby cancel this transaction.</p>

    <div class="tp-signature-line">
      <span class="tp-label">Customer's Signature:</span>
      <span class="tp-fill tp-fill-lg" data-sig-role="signature"></span>
      <span class="tp-label">Date:</span>
      <span class="tp-fill tp-fill-sm" data-sig-role="date"></span>
    </div>
    <div class="tp-signature-line">
      <span class="tp-label">Property Address:</span>
      <span class="tp-fill tp-fill-xl">\${lead.streetAddress}</span>
    </div>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 13 of 14 --</div>
  </section>

  <!-- ============================= PAGE 14: DUPLICATE COPY ============================= -->
  <section class="tp-page">
    <div class="text-center font-bold">
      <div>(DUPLICATE COPY)</div>
      <div>NOTICE OF RIGHT TO CANCEL</div>
      <div>AND</div>
      <div>NOTICE OF CANCELATION</div>
    </div>

    <p class="mt-6">You may cancel this transaction, without any penalty or obligation, within three business days from the Agreement date.</p>
    <p class="mt-2">If you cancel, any property traded in, any payments made by you under the contract or sale, and any negotiable instrument executed by you will be returned within 10 days following receipt by the seller of your cancellation notice, and any security interest arising out of the transaction will be canceled.</p>
    <p class="mt-2">If you cancel, you must make available to the seller at your residence, in substantially as good condition as when received, any goods delivered to you under this contract or sale, or you may, if you wish, comply with the instructions of the seller regarding the return shipment of the goods at the seller's expense and risk.</p>
    <p class="mt-2">If you do make the goods available to the seller and the seller does not pick them up within 20 days of the date of your notice of cancellation, you may retain or dispose of the goods without any further obligation.</p>
    <p class="mt-2">If you fail to make the goods available to the seller, or if you agree to return the goods to the seller and fail to do so, then you remain liable for performance of all obligations under the contract.</p>
    <p class="mt-2">To cancel this transaction, mail, email, fax, or deliver a signed and dated copy of this cancellation notice, or any other written notice to:</p>

    <p class="font-bold mt-6">Cancellation by Buyer:</p>
    <p class="mt-2">Deliver to Topsail Roofing and Construction Inc.</p>
    <p>not later than midnight of <span class="tp-fill tp-fill-sm" data-agreement-field="cancellationDeadline">\${agreementDetails.cancellationDeadline}</span> (3 business days after date of transaction).</p>
    <p class="mt-2">PO Box 3427, San Clemente CA 92674</p>
    <p><a href="mailto:Fletcher@topsailroofing.com" class="text-blue-600 underline">Fletcher@topsailroofing.com</a></p>
    <p>Fax: 949-807-0122</p>

    <p class="mt-6 tp-italic">I hereby cancel this transaction.</p>

    <div class="tp-signature-line">
      <span class="tp-label">Customer's Signature:</span>
      <span class="tp-fill tp-fill-lg" data-sig-role="signature"></span>
      <span class="tp-label">Date:</span>
      <span class="tp-fill tp-fill-sm" data-sig-role="date"></span>
    </div>
    <div class="tp-signature-line">
      <span class="tp-label">Property Address:</span>
      <span class="tp-fill tp-fill-xl">\${lead.streetAddress}</span>
    </div>

    <div class="tp-initials-row">
      <span>Topsail Representative Initials: <span class="tp-fill tp-fill-xs" data-rep-initials="true">\${topsailRepInitials}</span></span>
      <span>Customer Initials: <span class="tp-fill tp-fill-xs" data-customer-initials="true">\${customerInitials}</span></span>
    </div>

    <div class="tp-page-num">-- 14 of 14 --</div>
  </section>

  <!-- Signature Section (empty by default; populated by JS when signature exists) -->
  \${signatureSection}
</div>
`;

// Finally export everything at once
export {
  DEFAULT_PROPOSAL_TEMPLATE,
  generateItemsHtml
};

// Sanitise an R2 asset URL: strip any accidental `/main/` segment that older
// upload middleware injected into the public path (see fileUtils.js). Any
// path that already looks correct is returned untouched. Keeps the estimate
// cover page from rendering broken <img>s for legacy uploads.
const sanitizeAssetUrl = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  return s.replace('/main/', '/');
};

// Collect photos attached to any scope group / cost code / standalone cost
// item and render them as a tile grid on the ESTIMATE cover page. Returns
// an empty string when there are no photos so the block collapses cleanly.
const buildEstimatePhotos = (items) => {
  const rows = Array.isArray(items) ? items : [];
  const collected = [];
  const walk = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach((row) => {
      if (!row) return;
      if (Array.isArray(row.photos)) {
        row.photos.forEach((p) => {
          const url = sanitizeAssetUrl(p?.fileUrl || p?.url || p?.location);
          if (url) collected.push({ url, name: p?.fileName || p?.originalname || '' });
        });
      }
      if (Array.isArray(row.costCodes)) walk(row.costCodes);
      // Some data shapes nest photos under costGroup.photos on cost items
      if (row.costGroup && Array.isArray(row.costGroup.photos)) {
        row.costGroup.photos.forEach((p) => {
          const url = sanitizeAssetUrl(p?.fileUrl || p?.url || p?.location);
          if (url) collected.push({ url, name: p?.fileName || p?.originalname || '' });
        });
      }
    });
  };
  walk(rows);
  if (!collected.length) return '';
  const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // De-dupe by URL — cost codes inside groups can otherwise repeat the
  // parent group's photos if the estimator attached them twice.
  const seen = new Set();
  const tiles = collected
    .filter((p) => {
      if (seen.has(p.url)) return false;
      seen.add(p.url);
      return true;
    })
    .map((p) => `
      <figure class="tp-estimate-photo">
        <img src="${esc(p.url)}" alt="${esc(p.name || 'Scope photo')}" />
        ${p.name ? `<figcaption>${esc(p.name)}</figcaption>` : ''}
      </figure>
    `)
    .join('');
  return `
    <div class="tp-estimate-photos-block">
      <div class="tp-estimate-scope-title">ATTACHED PHOTOS</div>
      <div class="tp-estimate-photo-grid">${tiles}</div>
    </div>
  `;
};

// Build the "COST $X,XXX" number shown on the ESTIMATE cover page. Uses the
// pre-computed proposal.clientPrice when available (works even after the
// server has stripped the raw items array), otherwise falls back to summing
// the item prices used to render the scope details.
const computeEstimateTotal = (proposal, items) => {
  const savedPrice = Number(proposal?.clientPrice);
  if (Number.isFinite(savedPrice) && savedPrice > 0) return savedPrice;
  return (items || []).reduce((sum, item) => {
    const price = item?.totals?.clientPrice ?? item?.clientPrice ?? 0;
    return sum + (Number(price) || 0);
  }, 0);
};

// Generate the "BALCONY DECK REPLACEMENT" style title + bulleted scope list
// shown on the ESTIMATE cover page.
//
// Each cost group (`type === 'scope'`) becomes its own labelled subsection:
//   <div class="tp-estimate-scope-title">GROUP NAME</div>
//   <p class="tp-estimate-scope-lead">optional description</p>
//   <ul><li>cost code 1  — qty unit — $price
//           <div class="tp-estimate-item-desc">description</div>
//       </li>...</ul>
//
// Standalone cost items (`type === 'cost'`) are collected into a single
// trailing "Additional Items" section (if there are any groups) or into
// their own list titled from the proposal (if there are no groups at all).
//
// The display settings the estimator toggles in the preview panel are all
// honoured here:
//   • showScopeDescription      → include group descriptions
//   • showCostCodeDescription   → include cost-code descriptions
//   • showQuantity / showUnit   → append qty / unit to each cost-code line
//   • showPrice                 → append per-line $ price
//   • expandGroups              → include cost-code bullets under groups
//                                 (when off, only the group title shows)
//   • separateGroups            → keep grouped and ungrouped lists visually
//                                 separated by an "ADDITIONAL ITEMS" heading
const buildEstimateScope = (items, proposal, settings = {}) => {
  const {
    showScopeDescription    = true,
    showCostCodeDescription = true,
    showQuantity            = false,
    showUnit                = false,
    showPrice               = false,
    expandGroups            = true,
    separateGroups          = false
  } = settings;

  const rows = Array.isArray(items) ? items : [];
  const groups = rows.filter((r) => (r.type || (r.costGroup ? 'scope' : 'cost')) === 'scope');
  const costs  = rows.filter((r) => (r.type || (r.costGroup ? 'scope' : 'cost')) === 'cost');

  const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const formatMoney = (n) => {
    const num = typeof n === 'string' ? parseFloat(n.replace(/[$,]/g, '')) : Number(n);
    if (!Number.isFinite(num)) return '';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const renderMeta = (item) => {
    const parts = [];
    const qty = item.quantity;
    if (showQuantity && qty !== undefined && qty !== null && String(qty).trim() !== '') {
      const unit = showUnit && item.unit ? ` ${esc(item.unit)}` : '';
      parts.push(`${esc(qty)}${unit}`);
    } else if (showUnit && item.unit) {
      parts.push(esc(item.unit));
    }
    if (showPrice) {
      const priceStr = formatMoney(item.clientPrice ?? item.totals?.clientPrice);
      if (priceStr) parts.push(priceStr);
    }
    return parts.length ? ` <span class="tp-estimate-item-meta">— ${parts.join(' · ')}</span>` : '';
  };

  const renderItemBullet = (item, { includeDescription }) => {
    const label = (item.item || item.name || item.title || '').trim();
    const desc  = (item.description || '').trim();
    // Guarantee something visible when the estimator forgot to fill in a
    // name — fall back to the description; if BOTH are empty, skip.
    if (!label && !desc) return '';
    const displayLabel = label || desc;
    const showSubDesc = includeDescription && desc && desc !== label && label;
    let inner = esc(displayLabel) + renderMeta(item);
    if (showSubDesc) {
      inner += `<div class="tp-estimate-item-desc">${esc(desc)}</div>`;
    }
    return `<li>${inner}</li>`;
  };

  const renderCostCodeList = (codes) => {
    const bullets = codes
      .map((cc) => renderItemBullet(cc, { includeDescription: showCostCodeDescription }))
      .filter(Boolean);
    if (!bullets.length) return '';
    return `<ul class="tp-estimate-scope-list">${bullets.join('')}</ul>`;
  };

  const renderStandaloneList = (list) => {
    const bullets = list
      .map((c) => renderItemBullet(c, { includeDescription: showScopeDescription }))
      .filter(Boolean);
    if (!bullets.length) return '';
    return `<ul class="tp-estimate-scope-list">${bullets.join('')}</ul>`;
  };

  const renderGroup = (group) => {
    const title = (group.item || group.name || group.title || '').trim().toUpperCase();
    const desc  = (group.description || '').trim();
    const codes = Array.isArray(group.costCodes) ? group.costCodes : [];

    let html = '';
    if (title) html += `<div class="tp-estimate-scope-title">${esc(title)}</div>`;
    if (showScopeDescription && desc) {
      html += `<p class="tp-estimate-scope-lead">${esc(desc)}</p>`;
    }
    if (expandGroups && codes.length) {
      html += renderCostCodeList(codes);
    }
    return html;
  };

  // Layout:
  //   • Grouped items first (with their per-group titles).
  //   • Ungrouped cost items appended after.
  //   • When separateGroups is ON and both blocks exist, add an
  //     "ADDITIONAL ITEMS" divider heading so the split is visually
  //     explicit. When OFF, ungrouped items flow immediately after the
  //     last group's list — no divider.
  let listHtml = groups.map(renderGroup).join('');
  if (costs.length) {
    if (groups.length && separateGroups) {
      listHtml += '<div class="tp-estimate-scope-title">ADDITIONAL ITEMS</div>';
    }
    listHtml += renderStandaloneList(costs);
  }

  // Outer title: empty when we've emitted per-group titles above; otherwise
  // fall back to the proposal title so the page never renders a bare bullet
  // list with no context.
  const outerTitle = groups.length
    ? ''
    : (proposal?.title || 'PROJECT SCOPE').trim().toUpperCase();

  return { title: outerTitle, listHtml };
};

// Update the template generation function
export const generateProposalTemplate = (lead, proposal, items, displaySettings = {}) => {
  // Ensure all display settings have default values
  const settings = {
    showItem: true,
    showQuantity: false,
    showUnit: false,
    showPrice: false,
    showCostCodes: false,
    showScopeDescription: true,
    showCostCodeDescription: true,
    showTotalCost: true,
    expandGroups: false,
    separateGroups: false,
    ...displaySettings
  };

  const itemsHtml = generateItemsHtml(items, settings);

  const clientPhone = lead.client?.cellPhone ||
    lead.client?.altPhone ||
    lead.siteContact?.cellPhone ||
    lead.siteContact?.altPhone ||
    lead.leadContact?.phone ||
    lead.clientPhone ||
    '';

  const clientEmail = lead.client?.email || lead.clientEmail || lead.leadContact?.email || '';

  // Compose "City, ST ZIP" so the cover page can show a single address line
  // without inserting stray commas when the parts are missing.
  const city = lead.siteContact?.address?.city || '';
  const state = lead.siteContact?.address?.state || '';
  const zip = lead.siteContact?.address?.zipCode || '';
  const cityLine = [city, state].filter(Boolean).join(', ');
  const cityLineFull = [cityLine, zip].filter(Boolean).join(' ');
  const cityLinePrefix = cityLineFull ? `, ${cityLineFull}` : '';

  const agreementDetails = proposal?.agreementDetails || {};
  const agreementCosts = agreementDetails.agreementCosts || {};

  const { title: estimateScopeTitle, listHtml: estimateScopeHtml } = buildEstimateScope(items, proposal, settings);
  const estimateTotal = computeEstimateTotal(proposal, items);
  const estimateCostRow = settings.showTotalCost === false
    ? ''
    : `<div class="tp-estimate-cost-row"><span class="tp-estimate-cost-label">COST</span><span class="tp-estimate-cost-value">$${formatCurrency(estimateTotal)}</span></div>`;
  const estimatePhotosHtml = buildEstimatePhotos(items);

  // "SUBMITTED BY" line — best-effort assembly from whatever contact info
  // is attached to the lead. Falls back gracefully when a field is empty.
  const repName = lead.assignedTo?.name || lead.createdBy?.name || 'Topsail Roofing + Construction';
  const repEmail = lead.assignedTo?.email || lead.createdBy?.email || 'fletcher@topsailroofing.com';
  const repPhone = lead.assignedTo?.phone || lead.createdBy?.phone || '(949) 370-8612';
  const submittedByLine = [repName, repEmail, repPhone].filter(Boolean).join(' | ');

  // Flatten the agreement-details object into `agreementDetails.<field>`
  // lookup keys the string-based template interpolation understands.
  const flatAgreementFields = {
    'agreementDetails.approxStartDate':      agreementDetails.approxStartDate      || '',
    'agreementDetails.approxCompletionDate': agreementDetails.approxCompletionDate || '',
    'agreementDetails.approxDuration':       agreementDetails.approxDuration       || '',
    'agreementDetails.progressPaymentUpon':  agreementDetails.progressPaymentUpon  || '',
    'agreementDetails.cancellationDeadline': agreementDetails.cancellationDeadline || ''
  };

  // Rep initials — saved on the proposal, else derived from assigned rep.
  const repInitials = resolveRepInitials(
    proposal.topsailRepInitials,
    proposal.createdBy || lead.assignedTo || lead.createdBy
  );

  // Contractor block (editable in the edit preview) + customer initials
  // (captured at signing). These are rendered into the template as literal
  // `\${...}` markers so we can swap them here at render time.
  const contractorSignatureImg = proposal.contractorSignature
    ? `<img src="${proposal.contractorSignature}" alt="Contractor Signature" class="h-10" style="max-height:40px; vertical-align:middle;" />`
    : '';
  const contractorName = proposal.contractorName || '';
  const parseSigDate = (raw) => {
    if (!raw) return '';
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  };
  const contractorDate = parseSigDate(proposal.signature?.date)
    || (proposal.contractorSignature ? new Date().toLocaleDateString() : '');
  const customerInitials = proposal.signature?.initials || '';

  const templateData = {
    'lead.clientName': lead.clientName || lead.client?.name || '',
    'lead.streetAddress': lead.siteContact?.address?.street || '',
    'lead.cityLine': cityLinePrefix,
    'lead.city': city,
    'lead.state': state,
    'lead.zipCode': zip,
    'lead.clientEmail': clientEmail,
    'lead.clientEmailLabel': (clientEmail.includes(',') ? 'Emails' : 'Email'),
    'lead.clientPhone': clientPhone,
    'lead.jobNumber': lead.jobNumber || '',
    'proposal.title': proposal.title || '',
    'today': new Date().toLocaleDateString(),
    'itemsHtml': itemsHtml,
    'estimateScopeTitle': estimateScopeTitle,
    'estimateScopeHtml': estimateScopeHtml,
    'estimateCostFormatted': formatCurrency(estimateTotal),
    'estimateCostRow': estimateCostRow,
    'estimatePhotosHtml': estimatePhotosHtml,
    'submittedByLine': submittedByLine,
    'totalClientPrice.toFixed(2)': items.reduce((sum, item) => sum + (Number(item.clientPrice) || 0), 0).toFixed(2),
    'topsailRepInitials': repInitials,
    'customerInitials': customerInitials,
    'contractorName': contractorName,
    'contractorSignatureImg': contractorSignatureImg,
    'contractorDate': contractorDate,
    'signatureSection': '',
    ...flatAgreementFields
  };


  // Replace all template variables
  let html = DEFAULT_PROPOSAL_TEMPLATE;
  Object.entries(templateData).forEach(([key, value]) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\$\\{${escapedKey}\\}`, 'g');
    html = html.replace(regex, value == null ? '' : value);
  });

  // Fill Agreement Costs table cells from proposal.agreementDetails.agreementCosts.
  // Uses simple string replacement on `data-agreement-cost="row.col"` cells
  // because no DOM is guaranteed here (server-side generation goes through
  // the same helper).
  const escapeHtml = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(
    /(<td\b[^>]*\bdata-agreement-cost="([a-zA-Z]+)\.([a-zA-Z]+)"[^>]*>)([^<]*)(<\/td>)/g,
    (_m, openTag, row, col, currentInner, closeTag) => {
      const value = agreementCosts?.[row]?.[col];
      if (value === undefined || value === null || value === '') return `${openTag}${currentInner}${closeTag}`;
      return `${openTag}${escapeHtml(value)}${closeTag}`;
    }
  );

  // If a signature exists, inject it into EVERY slot marked with
  // data-sig-role (Acceptance page + both Notice-of-Right-to-Cancel
  // pages). The previous implementation only filled the first three
  // `.border-b.border-gray-300.pb-2` elements, which meant the Customer
  // Signature / Date on pages 13 and 14 stayed blank in the internal
  // preview even after the customer had signed via the public link.
  if (proposal.signature && typeof document !== 'undefined') {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const setSlot = (slot, buildInner) => {
        // Some slots wrap their contents in an inner <div> to keep a
        // fixed writing surface; write into that when present so we
        // don't collapse the underline.
        const target = slot.querySelector('div') || slot;
        target.innerHTML = buildInner;
      };

      if (proposal.signature.signatureImage) {
        const imgHtml = `<img src="${proposal.signature.signatureImage}" alt="Signature" class="h-10" style="max-height:40px; vertical-align:middle;" />`;
        tempDiv.querySelectorAll('[data-sig-role="signature"]').forEach((slot) => setSlot(slot, imgHtml));
      }
      const dateStr = parseSigDate(proposal.signature.date);
      if (dateStr) {
        tempDiv.querySelectorAll('[data-sig-role="date"]').forEach((slot) => setSlot(slot, dateStr));
      }
      if (proposal.signature.name) {
        tempDiv.querySelectorAll('[data-sig-role="name"]').forEach((slot) => setSlot(slot, proposal.signature.name));
      }

      html = tempDiv.innerHTML;
    } catch (error) {
      console.error('Error injecting signature into template:', error);
    }
  }

  const cleanProposalHtml = (h) => {
    if (!h || typeof h !== 'string') return h;
    // Canonicalise the Topsail logo URL across legacy R2 buckets.
    //
    // Important: match `/logo/…` as a DISTINCT path segment (leading `/`)
    // so filenames that merely contain the substring "logo" (e.g. a user
    // upload named `hfslogo72_….png`) are left untouched. The previous
    // pattern `[^\s"'>]*logo[^\s"'>]*` was greedy enough to rewrite any
    // uploaded scope photo whose filename happened to include "logo",
    // which swapped attached photos on the estimate cover for the
    // Topsail wordmark. The second branch rewrites the entire contents
    // of a specific decommissioned bucket that only ever hosted logos.
    return h.replace(
      /https:\/\/(pub-[a-f0-9]+\.r2\.dev(?:\/[^\s"'>]*)?\/logo\/[^\s"'>]+|pub-d6f1bdf0d5ad470d9760c07d060062bf\.r2\.dev\/[^\s"'>]+)/gi,
      'https://pub-7850b80959a348cea25cdb70de6e8ddc.r2.dev/logo/Topsail-logo_RGB-03.png'
    );
  };

  return cleanProposalHtml(html);
};
